// src/components/EmailEditor.jsx
import React, { useState, useRef, useEffect } from 'react';

const EmailEditor = ({ isOpen, onClose, onSend, selectedCount, loading }) => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [activeTab, setActiveTab] = useState('templates'); // templates, editor, preview
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [customTemplates, setCustomTemplates] = useState([]);
  const editorRef = useRef(null);

  // Prostsze szablony email
  const templates = [
    {
      id: 'welcome',
      name: 'Powitanie',
      icon: '👋',
      category: 'Ogólne',
      subject: 'Witamy w BoHoBali!',
      preview: 'Ciepłe powitanie dla nowych klientów',
      content: `<div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; background: #ffffff;">
        <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 30px; text-align: center; border-radius: 12px 12px 0 0;">
          <h1 style="color: white; margin: 0 0 12px 0; font-size: 28px; font-weight: 600;">Witamy w BoHoBali!</h1>
          <p style="color: rgba(255,255,255,0.9); margin: 0; font-size: 16px;">Cieszymy się, że do nas dołączyłeś</p>
        </div>
        <div style="padding: 40px 30px; background: white; border-radius: 0 0 12px 12px;">
          <p style="color: #2d3748; font-size: 16px; line-height: 1.5;">Dziękujemy za zaufanie! Oferujemy profesjonalne usługi fryzjerskie, manicure, pedicure i zabiegi kosmetyczne.</p>
          <div style="text-align: center; margin: 32px 0;">
            <a href="#" style="background: linear-gradient(135deg, #667eea, #764ba2); color: white; padding: 16px 32px; text-decoration: none; border-radius: 8px; font-weight: 500; display: inline-block;">
              Umów wizytę
            </a>
          </div>
          <p style="text-align: center; color: #718096; font-size: 14px;">
            Pozdrawiamy,<br><strong>Zespół BoHoBali</strong>
          </p>
        </div>
      </div>`
    },
    {
      id: 'promotion',
      name: 'Promocja',
      icon: '🎯',
      category: 'Marketing',
      subject: '🔥 Specjalna promocja tylko dla Ciebie!',
      preview: 'Szablon do promocji i ofert specjalnych',
      content: `<div style="max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; background: #ffffff;">
        <div style="background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%); padding: 40px 30px; text-align: center; border-radius: 12px 12px 0 0;">
          <h1 style="color: white; margin: 0 0 12px 0; font-size: 32px; font-weight: 700;">🔥 PROMOCJA 🔥</h1>
          <p style="color: rgba(255,255,255,0.95); margin: 0; font-size: 18px;">Specjalna oferta tylko dla Ciebie!</p>
        </div>
        <div style="background: white; padding: 40px 30px; border-radius: 0 0 12px 12px;">
          <div style="background: #2d3748; color: white; padding: 24px; border-radius: 12px; text-align: center; margin-bottom: 24px;">
            <div style="font-size: 48px; font-weight: 800; margin: 0 0 8px 0;">-30%</div>
            <p style="margin: 0; font-size: 16px;">NA WSZYSTKIE USŁUGI</p>
          </div>
          <div style="text-align: center; margin: 24px 0;">
            <div style="background: #ff6b6b; color: white; padding: 16px 24px; border-radius: 8px; display: inline-block; font-size: 18px; font-weight: 600;">
              KOD: PROMO30
            </div>
          </div>
          <div style="text-align: center;">
            <a href="#" style="background: #ff6b6b; color: white; padding: 18px 36px; text-decoration: none; border-radius: 8px; font-weight: 600; display: inline-block;">
              Skorzystaj z promocji
            </a>
          </div>
        </div>
      </div>`
    }
  ];

  const categories = ['Wszystkie', 'Ogólne', 'Marketing', 'Automatyczne'];
  const [selectedCategory, setSelectedCategory] = useState('Wszystkie');

  const filteredTemplates = selectedCategory === 'Wszystkie' 
    ? templates 
    : templates.filter(template => template.category === selectedCategory);

  useEffect(() => {
    const saved = localStorage.getItem('emailTemplates');
    if (saved) {
      setCustomTemplates(JSON.parse(saved));
    }
  }, []);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setSubject(template.subject);
    setContent(template.content);
    setActiveTab('editor');
  };

  const handleSaveTemplate = () => {
    if (!templateName.trim()) return;
    
    const newTemplate = {
      id: Date.now().toString(),
      name: templateName,
      icon: '',
      category: 'Własne',
      subject,
      preview: 'Własny szablon',
      content
    };
    
    const updated = [...customTemplates, newTemplate];
    setCustomTemplates(updated);
    localStorage.setItem('emailTemplates', JSON.stringify(updated));
    setSaveAsTemplate(false);
    setTemplateName('');
  };

  const handleSend = () => {
    if (!subject.trim() || !content.trim()) {
      alert('Proszę wypełnić temat i treść wiadomości');
      return;
    }
    onSend({ subject, content });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-200 p-6 flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900"> Edytor wiadomości email</h2>
            <p className="text-gray-600 mt-1">
              Wyślij wiadomość do <span className="font-semibold text-blue-600">{selectedCount}</span> wybranych klientów
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'templates', label: ' Szablony' },
              { id: 'editor', label: ' Edytor' },
              { id: 'preview', label: ' Podgląd' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content - tylko jeden widok na raz */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'templates' && (
            <div className="h-full p-6 overflow-y-auto">
              {/* Category Filter */}
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        selectedCategory === category
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Templates Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-3xl">{template.icon}</div>
                      <div className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                        {template.category}
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {template.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3">{template.preview}</p>
                    <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded-md">
                      <strong>Temat:</strong> {template.subject}
                    </div>
                  </div>
                ))}

                {/* Custom Templates */}
                {customTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-3xl">{template.icon}</div>
                      <div className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full">
                        Własny
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                      {template.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3">{template.preview}</p>
                    <div className="text-xs text-gray-500 bg-white p-2 rounded-md">
                      <strong>Temat:</strong> {template.subject}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'editor' && (
            <div className="h-full p-6 overflow-y-auto">
              <div className="max-w-4xl mx-auto space-y-6">
                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temat wiadomości
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Wprowadź temat wiadomości..."
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Content */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Treść wiadomości (HTML)
                    </label>
                    <button
                      onClick={() => setSaveAsTemplate(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span>Zapisz jako szablon</span>
                    </button>
                  </div>
                  <textarea
                    ref={editorRef}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Wprowadź treść wiadomości w formacie HTML..."
                    className="w-full h-96 border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  />
                  <div className="mt-2 text-xs text-gray-500">
                    Możesz używać HTML do formatowania wiadomości. Przejdź do zakładki Podgląd, aby zobaczyć wynik.
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Szybkie akcje</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <button
                      onClick={() => setContent(content + '<strong>Pogrubiony tekst</strong>')}
                      className="p-2 bg-white border border-gray-200 rounded text-sm hover:bg-gray-50"
                    >
                      <strong>B</strong> Pogrubienie
                    </button>
                    <button
                      onClick={() => setContent(content + '<em>Kursywa</em>')}
                      className="p-2 bg-white border border-gray-200 rounded text-sm hover:bg-gray-50"
                    >
                      <em>I</em> Kursywa
                    </button>
                    <button
                      onClick={() => setContent(content + '<a href="https://bohobali.pl" style="color: #3b82f6;">Link</a>')}
                      className="p-2 bg-white border border-gray-200 rounded text-sm hover:bg-gray-50"
                    >
                       Link
                    </button>
                    <button
                      onClick={() => setContent(content + '<br><br>')}
                      className="p-2 bg-white border border-gray-200 rounded text-sm hover:bg-gray-50"
                    >
                       Nowa linia
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="h-full p-6 overflow-y-auto bg-gray-50">
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
                  <div className="mb-6 pb-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-500">Od: BoHoBali &lt;noreply@bohobali.pl&gt;</div>
                        <div className="text-sm text-gray-500">Do: Wybrani klienci ({selectedCount})</div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date().toLocaleDateString('pl-PL')}
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="text-xl font-semibold text-gray-900">{subject || 'Brak tematu'}</div>
                    </div>
                  </div>
                  <div 
                    className="prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: content || '<p class="text-gray-400">Brak treści wiadomości</p>' }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {selectedTemplate && (
              <div className="text-sm text-gray-600">
                Używasz szablonu: <span className="font-medium">{selectedTemplate.name}</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Anuluj
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              disabled={!subject.trim() || !content.trim()}
            >
              Podgląd
            </button>
            <button
              onClick={handleSend}
              disabled={!subject.trim() || !content.trim() || loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Wysyłanie...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span>Wyślij ({selectedCount})</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Save Template Modal */}
        {saveAsTemplate && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg p-6 w-96">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Zapisz jako szablon</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nazwa szablonu
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Wprowadź nazwę szablonu..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setSaveAsTemplate(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleSaveTemplate}
                  disabled={!templateName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Zapisz
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailEditor;
